/**
 * Navigation Constants
 * Centralized location for all navigation routes and URLs
 */

/**
 * Authentication routes
 */
export const AUTH_ROUTES = {
  BASE: '/auth',
  LOGIN: '/auth/login',
  LOGOUT: '/auth/logout',
  REGISTER: '/auth/register',
  FORGOT_PASSWORD: '/auth/forgot-password',
  RESET_PASSWORD: '/auth/reset-password',
  VERIFY_EMAIL: '/auth/verify-email',
  OTP_VERIFICATION: '/auth/otp-verification',
} as const;

/**
 * Main application routes
 */
export const APP_ROUTES = {
  HOME: '/',
  DASHBOARD: '/dashboard',
  ACCOUNT: '/dashboard/accounts',
  TRANSACTIONS: '/transactions',
  TRANSFERS_PAYMENTS: '/payments',
  BENEFICIARIES: '/beneficiaries',
  CARDS: '/cards',
  LOANS_CREDIT: '/loans-credit',
  INVESTMENTS: '/investments',
  REPORTS_STATEMENTS: '/reports',
  LOYALTY: '/loyalty',
  PROFILE: '/profile',
  SETTINGS: '/settings',
  NOT_FOUND: '/404',
} as const;

/**
 * Dashboard sub-routes
 */
export const DASHBOARD_ROUTES = {
  BASE: '/dashboard',
  OVERVIEW: '/dashboard/overview',
  ANALYTICS: '/dashboard/analytics',
  REPORTS: '/dashboard/reports',
  NOTIFICATIONS: '/dashboard/notifications',
} as const;

/**
 * Beneficiaries routes
 */
export const BENEFICIARY_ROUTES = {
  BASE: '/beneficiaries',
  LIST: '/beneficiaries',
  CREATE: '/beneficiaries/create',
  EDIT: (id: string | number) => `/beneficiaries/${id}/edit`,
  VIEW: (id: string | number) => `/beneficiaries/${id}`,
  SEARCH: '/beneficiaries/search',
} as const;

/**
 * Transaction route paths (for route configuration)
 * These are relative paths used in route definitions
 */
export const TRANSACTION_ROUTE_PATHS = {
  DOMESTIC: 'domestic',
  DOMESTIC_DETAILS: 'domestic/details',
  DOMESTIC_REVIEW: 'domestic/review',
  DOMESTIC_VERIFY_OTP: 'domestic/verify-otp',
  DOMESTIC_SUCCESS: 'domestic/success',
  INTERNATIONAL: 'international',
  INTERNATIONAL_DETAILS: 'international/details',
  INTERNATIONAL_REVIEW: 'international/review',
  INTERNATIONAL_VERIFY_OTP: 'international/verify-otp',
  INTERNATIONAL_SUCCESS: 'international/success',
  INTERNATIONAL_HISTORY: 'international/history',
  INTERNATIONAL_TRANSACTION_DETAILS: 'international/transaction-details/:paymentId',
  INTERNATIONAL_SWIFT_STATUS: 'international/swift-status/:swiftRef',
  // P2P Transfers
  P2P: 'p2p',
  // QR Payments
  QR: 'qr',
} as const;

/**
 * Transaction routes (formerly Payment routes)
 * These are absolute paths used for navigation
 */
export const TRANSACTION_ROUTES = {
  BASE: '/transactions',
  DOMESTIC: '/transactions/domestic',
  DOMESTIC_DETAILS: '/transactions/domestic/details',
  DOMESTIC_REVIEW: '/transactions/domestic/review',
  DOMESTIC_VERIFY_OTP: '/transactions/domestic/verify-otp',
  DOMESTIC_SUCCESS: '/transactions/domestic/success',
  INTERNATIONAL: '/transactions/international',
  INTERNATIONAL_DETAILS: '/transactions/international/details',
  INTERNATIONAL_REVIEW: '/transactions/international/review',
  INTERNATIONAL_VERIFY_OTP: '/transactions/international/verify-otp',
  INTERNATIONAL_SUCCESS: '/transactions/international/success',
  INTERNATIONAL_HISTORY: '/transactions/international/history',
  INTERNATIONAL_TRANSACTION_DETAILS: (paymentId: string | number) =>
    `/transactions/international/transaction-details/${paymentId}`,
  INTERNATIONAL_SWIFT_STATUS: (swiftRef: string) =>
    `/transactions/international/swift-status/${swiftRef}`,
} as const;

/**
 * Payment routes - Transfers & Payments section
 */
export const PAYMENT_ROUTES = {
  BASE: '/payments',
  // Domestic Payments
  DOMESTIC: '/payments/domestic',
  DOMESTIC_DETAILS: '/payments/domestic/details',
  DOMESTIC_REVIEW: '/payments/domestic/review',
  DOMESTIC_VERIFY_OTP: '/payments/domestic/verify-otp',
  DOMESTIC_SUCCESS: '/payments/domestic/success',
  // International Payments
  INTERNATIONAL: '/payments/international',
  INTERNATIONAL_DETAILS: '/payments/international/details',
  INTERNATIONAL_REVIEW: '/payments/international/review',
  INTERNATIONAL_VERIFY_OTP: '/payments/international/verify-otp',
  INTERNATIONAL_SUCCESS: '/payments/international/success',
  INTERNATIONAL_HISTORY: '/payments/international/history',
  INTERNATIONAL_TRANSACTION_DETAILS: (paymentId: string | number) =>
    `/payments/international/transaction-details/${paymentId}`,
  // P2P Transfers
  P2P: '/payments/p2p',
  // QR Payments
  QR: '/payments/qr',
  // Bill Payments
  BILL: '/bill-payment',
  // Legacy routes
  LIST: '/payments',
  CREATE: '/payments/create',
  HISTORY: '/payments/history',
  VIEW: (id: string | number) => `/payments/${id}`,
  RECEIPT: (id: string | number) => `/payments/${id}/receipt`,
} as const;

/**
 * Investment routes
 */
export const INVESTMENT_ROUTES = {
  BASE: '/investments',
  DASHBOARD: '/investments',
  PORTFOLIO: '/investments/portfolio',
  STOCKS: '/investments/stocks',
  MUTUAL_FUNDS: '/investments/mutual-funds',
} as const;

/**
 * Personal Finance Management routes
 */
export const PFM_ROUTES = {
  BASE: '/personal-finance',
  OVERVIEW: '/personal-finance/overview',
  BUDGETS: '/personal-finance/budgets',
  GOALS: '/personal-finance/goals',
  DEBTS: '/personal-finance/debts',
  NET_WORTH: '/personal-finance/net-worth',
  INSIGHTS: '/personal-finance/insights',
} as const;

/**
 * Loan routes
 */
export const LOAN_ROUTES = {
  BASE: '/loans-credit',
  MY_LOANS: '/loans-credit',
  APPLY: '/loans-credit/apply',
};

export const LOYALTY_ROUTES_CONST = {
  BASE: '/loyalty',
  DASHBOARD: '/loyalty',
  REWARDS: '/loyalty/rewards',
  HISTORY: '/loyalty/history',
} as const;

/**
 * Settings routes
 */
export const SETTINGS_ROUTES = {
  BASE: '/settings',
  GENERAL: '/settings/general',
  SECURITY: '/settings/security',
  NOTIFICATIONS: '/settings/notifications',
  LANGUAGE: '/settings/language',
  THEME: '/settings/theme',
  PROFILE: '/settings/profile',
} as const;

/**
 * Onboarding routes
 */
export const ONBOARDING_ROUTES = {
  BASE: '/onboarding',
  OTP_VERIFICATION: '/onboarding/otp-verification',
  KYC_SELECTION: '/onboarding/kyc-selection',
  DOCUMENT_CAPTURE: '/onboarding/document-capture',
  // eKYC Flow Routes
  SELFIE_CAPTURE: '/onboarding/selfie-capture',
  VERIFY_DETAILS: '/onboarding/verify-details',
  AML_CHECK: '/onboarding/aml-check',
  ACCOUNT_SETUP: '/onboarding/account-setup',
  // Legacy routes (kept for backward compatibility)
  PERSONAL_DETAILS: '/onboarding/personal-details',
  ADDRESS_DETAILS: '/onboarding/address-details',
  REVIEW_SUBMIT: '/onboarding/review-submit',
  COMPLETE: '/onboarding/complete',
} as const;

/**
 * All navigation routes grouped together
 */
export const NAVIGATION_ROUTES = {
  AUTH: AUTH_ROUTES,
  APP: APP_ROUTES,
  DASHBOARD: DASHBOARD_ROUTES,
  BENEFICIARY: BENEFICIARY_ROUTES,
  TRANSACTION: TRANSACTION_ROUTES,
  LOAN: LOAN_ROUTES,
  PAYMENT: PAYMENT_ROUTES,
  INVESTMENT: INVESTMENT_ROUTES,
  PFM: PFM_ROUTES,
  LOYALTY: LOYALTY_ROUTES_CONST,
  SETTINGS: SETTINGS_ROUTES,
  ONBOARDING: ONBOARDING_ROUTES,
} as const;

/**
 * External URLs
 */
export const EXTERNAL_URLS = {
  SUPPORT: 'https://support.tradeplatform.com',
  DOCUMENTATION: 'https://docs.tradeplatform.com',
  PRIVACY_POLICY: 'https://tradeplatform.com/privacy',
  TERMS_OF_SERVICE: 'https://tradeplatform.com/terms',
  CONTACT: 'https://tradeplatform.com/contact',
} as const;
